import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, Form, Button, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { useCreateMeter, useUpdateMeter, useKPIs } from '../hooks/useMeters';

const MeterForm = forwardRef(({ onSave, sources, subLocations }, ref) => {
  const [show, setShow] = useState(false);
  const [currentMeter, setCurrentMeter] = useState(null);
  const [formData, setFormData] = useState({
    meterId: '',
    meterName: '',
    meterType: 'WATER_METER',
    gatewayId: '',
    sourceId: 0,
    subLocationId: 0,
    unit: '',
    measurementType: 'FLOW',
    inactiveDurationMinutes: 15,
    kpiId: 0 // For current financial year
  });
  const [errors, setErrors] = useState({});

  const createMutation = useCreateMeter();
  const updateMutation = useUpdateMeter(currentMeter?.meterId || '');
  
  // Fetch EXTERNAL KPIs only (users can only configure EXTERNAL KPIs)
  const { data: kpis = [], isLoading: kpisLoading } = useKPIs({ kpiType: 'EXTERNAL' });

  useImperativeHandle(ref, () => ({
    show: (meter) => {
      if (meter) {
        setCurrentMeter(meter);
        // Get current FY KPI
        const currentFYKpi = meter.kpiMappings?.find(
          mapping => mapping.financialYear === '2025-2026'
        );
        
        setFormData({
          meterId: meter.meterId,
          meterName: meter.meterName,
          meterType: meter.meterType,
          gatewayId: meter.gatewayId,
          sourceId: meter.sourceId,
          subLocationId: meter.subLocationId,
          unit: meter.unit,
          measurementType: meter.measurementType,
          inactiveDurationMinutes: meter.inactiveDurationMinutes ?? 15,
          kpiId: currentFYKpi?.kpiId || 0
        });
      } else {
        setCurrentMeter(null);
        setFormData({
          meterId: '',
          meterName: '',
          meterType: 'WATER_METER',
          gatewayId: '',
          sourceId: 0,
          subLocationId: 0,
          unit: '',
          measurementType: 'FLOW',
          inactiveDurationMinutes: 15,
          kpiId: 0
        });
      }
      setErrors({});
      setShow(true);
    },
    hide: () => {
      setShow(false);
      setCurrentMeter(null);
      setErrors({});
    }
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.meterId) newErrors.meterId = 'Meter ID is required';
    if (!formData.meterName) newErrors.meterName = 'Meter Name is required';
    if (!formData.gatewayId) newErrors.gatewayId = 'Gateway ID is required';
    if (!formData.sourceId) newErrors.sourceId = 'Location is required';
    if (!formData.subLocationId) newErrors.subLocationId = 'Sub Location is required';
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (formData.inactiveDurationMinutes === undefined || formData.inactiveDurationMinutes < 1) {
      newErrors.inactiveDurationMinutes = 'Inactive duration must be at least 1 minute';
    }

    // KPI is NOT required during creation, only when editing

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    try {
      const submitData = {
        meterId: formData.meterId,
        meterName: formData.meterName,
        meterType: formData.meterType,
        gatewayId: formData.gatewayId,
        sourceId: formData.sourceId,
        subLocationId: formData.subLocationId,
        unit: formData.unit,
        measurementType: formData.measurementType,
        inactiveDurationMinutes: formData.inactiveDurationMinutes ?? 15
      };

      // Only include KPI mapping if KPI is selected
      if (formData.kpiId) {
        submitData.kpiId = formData.kpiId;
        submitData.financialYear = '2025-2026'; // Current FY
      }

      if (currentMeter) {
        await updateMutation.mutateAsync(submitData);
      } else {
        await createMutation.mutateAsync(submitData);
      }
      setShow(false);
      onSave();
    } catch (error) {
      console.error('Error saving meter:', error);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const error = createMutation.error || updateMutation.error;

  // Group KPIs by hierarchy
  const groupedKPIs = kpis.reduce((acc, kpi) => {
    const key = `${kpi.module} > ${kpi.category}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(kpi);
    return acc;
  }, {});

  return (
    <Modal 
      show={show} 
      onHide={() => setShow(false)} 
      size="lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>{currentMeter ? 'Edit Meter' : 'Add New Meter'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger">
              {error instanceof Error ? error.message : 'An error occurred'}
            </Alert>
          )}

          {/* Basic Information */}
          <h6 className="mb-3">Basic Information</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.meterId}
                  onChange={(e) => handleChange('meterId', e.target.value)}
                  isInvalid={!!errors.meterId}
                  disabled={!!currentMeter}
                  placeholder="e.g., 3K822500393969"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.meterId}
                </Form.Control.Feedback>
                {currentMeter && (
                  <Form.Text className="text-muted">
                    Meter ID cannot be changed
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter Name *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.meterName}
                  onChange={(e) => handleChange('meterName', e.target.value)}
                  isInvalid={!!errors.meterName}
                  placeholder="e.g., Main Building Water Meter"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.meterName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Gateway ID *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.gatewayId}
                  onChange={(e) => handleChange('gatewayId', e.target.value)}
                  isInvalid={!!errors.gatewayId}
                  disabled={!!currentMeter}
                  placeholder="e.g., GTW-001"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.gatewayId}
                </Form.Control.Feedback>
                {currentMeter && (
                  <Form.Text className="text-muted">
                    Gateway ID cannot be changed
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Unit *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.unit}
                  onChange={(e) => handleChange('unit', e.target.value)}
                  isInvalid={!!errors.unit}
                  disabled={!!currentMeter}
                  placeholder="e.g., kL, kWh, m³"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.unit}
                </Form.Control.Feedback>
                {currentMeter && (
                  <Form.Text className="text-muted">
                    Unit cannot be changed
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Meter Type *</Form.Label>
                <Form.Select
                  value={formData.meterType}
                  onChange={(e) => handleChange('meterType', e.target.value)}
                >
                  <option value="WATER_METER">Water Meter</option>
                  <option value="ENERGY_METER">Energy Meter</option>
                  <option value="GAS_METER">Gas Meter</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Measurement Type *</Form.Label>
                <Form.Select
                  value={formData.measurementType}
                  onChange={(e) => handleChange('measurementType', e.target.value)}
                >
                  <option value="CUMULATIVE">CUMULATIVE</option>
                  <option value="INTERVAL">INTERVAL</option>
                  <option value="INSTANT">INSTANT</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Location Information */}
          <h6 className="mb-3 mt-4">Location Information</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Location *</Form.Label>
                <Form.Select
                  value={formData.sourceId}
                  onChange={(e) => handleChange('sourceId', parseInt(e.target.value))}
                  isInvalid={!!errors.sourceId}
                >
                  <option value="">Select Location</option>
                  {sources && sources.map(source => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.sourceId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Sub Location *</Form.Label>
                <Form.Select
                  value={formData.subLocationId}
                  onChange={(e) => handleChange('subLocationId', parseInt(e.target.value))}
                  isInvalid={!!errors.subLocationId}
                >
                  <option value="">Select Sub Location</option>
                  {subLocations && subLocations.map(subLocation => (
                    <option key={subLocation.value} value={subLocation.value}>
                      {subLocation.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.subLocationId}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {/* KPI Configuration - Optional during creation, can be added later */}
          <h6 className="mb-3 mt-4">
            KPI Configuration (Current Financial Year)
            {!currentMeter && <small className="text-muted ms-2">(Optional - can be configured later)</small>}
          </h6>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Select KPI (EXTERNAL) {!currentMeter && <span className="text-muted">(Optional)</span>}
                </Form.Label>
                {kpisLoading ? (
                  <div className="text-center p-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Loading KPIs...</span>
                  </div>
                ) : (
                  <Form.Select
                    value={formData.kpiId}
                    onChange={(e) => handleChange('kpiId', parseInt(e.target.value))}
                    isInvalid={!!errors.kpiId}
                  >
                    <option value="">Select KPI (Optional)</option>
                    {Object.entries(groupedKPIs).map(([group, kpiList]) => (
                      <optgroup key={group} label={group}>
                        {kpiList.map(kpi => (
                          <option key={kpi.id} value={kpi.id}>
                            {kpi.subCategory ? `${kpi.subCategory} > ` : ''}{kpi.kpi}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Form.Select>
                )}
                <Form.Control.Feedback type="invalid">
                  {errors.kpiId}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {currentMeter 
                    ? "Only EXTERNAL KPIs can be configured. INTERNAL KPIs (Volume Flow, Mass Totalizer) are system-managed."
                    : "KPI configuration is optional during creation. You can configure it later by editing the meter. Only EXTERNAL KPIs can be configured."
                  }
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Inactivity Settings */}
          <h6 className="mb-3 mt-4">Inactivity Settings</h6>

          <Form.Group className="mb-3">
            <Form.Label>Inactive Duration (minutes) *</Form.Label>
            <Form.Control
              type="number"
              min={1}
              value={formData.inactiveDurationMinutes}
              onChange={(e) =>
                handleChange(
                  'inactiveDurationMinutes',
                  parseInt(e.target.value, 10) || 1
                )
              }
              isInvalid={!!errors.inactiveDurationMinutes}
            />
            <Form.Control.Feedback type="invalid">
              {errors.inactiveDurationMinutes}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              The meter will be marked inactive if no data is received for this duration.
            </Form.Text>
          </Form.Group>

        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              currentMeter ? 'Update Meter' : 'Create Meter'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
});

MeterForm.displayName = 'MeterForm';

export default MeterForm;